/**
 * SearchableSelect — single-select dropdown with a search input inside.
 * Combines Popover + Command for the "long list" case where a plain
 * <Select> becomes hard to scan.
 *
 * Pass `onCreateOption` to let the user add a value that is not in the list
 * yet, offered inline against whatever they have typed.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { defaultFilter } from "cmdk";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import { CommandHighlightMirror } from "./command-highlight-mirror";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Tooltip / aria-label for long labels. */
  title?: string;
  /**
   * Muted context shown after the label in the list (a job code, a date):
   * what tells two same-named options apart. Not shown in the trigger.
   */
  description?: string;
  /**
   * Extra search terms beyond the label — the description, a code — so the
   * search input finds an option by its context, not only its name.
   */
  keywords?: string[];
  /**
   * Optional leading element (e.g. a user avatar). When set, the selected
   * option renders as a soft badge (avatar + label) in the trigger, and the
   * avatar shows beside each option in the list.
   */
  avatar?: ReactNode;
  /** Shown beside the label in the list only (e.g. a colour dot). */
  leading?: ReactNode;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Label for the "match all" sentinel option. Omit to hide it. */
  allOptionLabel?: string;
  /** Value used for the "all" sentinel option. Defaults to "all". */
  allOptionValue?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  /** Width applied to the popover content. Defaults to the trigger width. */
  contentClassName?: string;
  /**
   * Preferred side of the trigger. Defaults to Radix's `bottom`. Pass
   * `avoidCollisions={false}` as well when the list must not flip (e.g. a
   * combobox near the bottom of a dialog that would otherwise open above).
   */
  side?: "top" | "right" | "bottom" | "left";
  avoidCollisions?: boolean;
  disabled?: boolean;
  /**
   * When set, search text that matches no existing option offers an item that
   * creates it. Receives the raw text; the caller decides what to persist.
   */
  onCreateOption?: (input: string) => void;
  /** Label for that item. Defaults to `Add "<input>"`. */
  createOptionLabel?: (input: string) => string;
  /**
   * The trigger renders as `role="combobox"`, which (per ARIA) only takes
   * its accessible name from an explicit label — never from its own visible
   * text — so a callsite whose only visual label is an unassociated
   * `<Label>` (no `htmlFor`) needs this to give the control a name at all.
   */
  "aria-label"?: string;
}

/**
 * Separates an option's label from its value inside the cmdk item value, so
 * two same-named options stay distinct items while the search still scores
 * the label alone (a job id would otherwise fuzzy-match almost anything).
 */
const ITEM_VALUE_SEPARATOR = "\u241f";

/**
 * The score the create item takes whenever it shows: just above hidden, so
 * it always renders last and never takes the keyboard selection from a
 * genuine match. The search text is the item's own value, which the default
 * scorer would rank a perfect match — above every option it should defer to.
 */
const CREATE_ITEM_SCORE = 1e-6;

/** The cmdk item value for an option: label for scoring, id for identity. */
function itemValue(option: SearchableSelectOption): string {
  return `${option.label}${ITEM_VALUE_SEPARATOR}${option.value}`;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  allOptionLabel,
  allOptionValue = "all",
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No matches.",
  className,
  contentClassName,
  side,
  avoidCollisions,
  disabled,
  onCreateOption,
  createOptionLabel,
  "aria-label": ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Where cmdk's highlight (keyboard selection) sits, read from its store:
  // cmdk moves it itself on open and on each search change, and only
  // reports through `onValueChange` while controlled.
  const [cmdkHighlight, setCmdkHighlight] = useState("");
  // A highlight this component imposed (see below). Undefined leaves cmdk
  // uncontrolled; once set, cmdk's own moves flow back through
  // `onValueChange` so the two never disagree.
  const [forcedHighlight, setForcedHighlight] = useState<string | undefined>();
  // The practitioner moved the highlight on purpose (arrow keys, pointer)
  // since they last typed — their choice, never overridden.
  const [userNavigated, setUserNavigated] = useState(false);

  const trimmedSearch = search.trim();
  const canCreate =
    onCreateOption !== undefined &&
    trimmedSearch.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmedSearch.toLowerCase());

  // The option cmdk ranks first for the current search — the same scoring
  // it applies — so the highlight can be moved onto it when the options
  // arrive after the practitioner has already typed (a pipeline list that
  // loads a beat late). cmdk keeps its highlight on whatever was selected,
  // which by then is the create item; Enter would create a by-name entity
  // instead of linking the match that now sits first in the list. Only a
  // typed search is scored: with none there is no create item to correct.
  const bestMatch = useMemo(() => {
    if (!trimmedSearch) return undefined;
    return options.reduce<{
      option: SearchableSelectOption;
      score: number;
    } | null>((best, option) => {
      const score = defaultFilter(option.label, search, option.keywords);
      if (score <= 0) return best;
      return best !== null && best.score >= score ? best : { option, score };
    }, null)?.option;
  }, [options, search, trimmedSearch]);
  const bestMatchValue = bestMatch ? itemValue(bestMatch) : undefined;
  const highlightOnCreate =
    canCreate && cmdkHighlight.toLowerCase() === trimmedSearch.toLowerCase();
  useEffect(() => {
    if (!open || userNavigated || !highlightOnCreate) return;
    if (bestMatchValue === undefined) return;
    setForcedHighlight(bestMatchValue);
  }, [open, userNavigated, highlightOnCreate, bestMatchValue]);

  const markNavigated = useCallback(() => setUserNavigated(true), []);
  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const navigationKey =
      ["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"].includes(
        event.key,
      ) ||
      (event.ctrlKey && (event.key === "n" || event.key === "p"));
    if (navigationKey) markNavigated();
  };
  const handleSearchChange = (next: string) => {
    setSearch(next);
    // Typing restarts the automatic highlight; a prior arrow move no
    // longer describes what the practitioner wants.
    setUserNavigated(false);
  };

  const selectedOption =
    value === allOptionValue
      ? undefined
      : options.find((o) => o.value === value);
  const selectedLabel =
    value === allOptionValue ? allOptionLabel : selectedOption?.label;

  return (
    <Popover
      modal={false}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch("");
          setForcedHighlight(undefined);
          setUserNavigated(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          title={value === allOptionValue ? undefined : selectedOption?.title}
          className={cn(
            "inline-flex h-8 items-center justify-between gap-2 px-3 text-xs",
            "rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-ring",
            "transition-colors hover:bg-muted/30",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          {selectedOption?.avatar ? (
            <Badge
              variant="soft"
              color="gray"
              size="sm"
              className="min-w-0 max-w-full gap-1"
            >
              {selectedOption.avatar}
              <span className="truncate">{selectedLabel}</span>
            </Badge>
          ) : (
            <span
              className={cn(
                "truncate text-left",
                !selectedLabel && "text-muted-foreground",
              )}
            >
              {selectedLabel ?? placeholder}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Match the trigger, not a fixed 260px: long matter names (and any
        // full-width field) were leaving empty space beside a clipped list.
        // `--radix-popover-trigger-width` is published by Radix on open.
        className={cn(
          "w-[var(--radix-popover-trigger-width)] p-0",
          contentClassName,
        )}
        align="start"
        side={side}
        avoidCollisions={avoidCollisions}
      >
        <Command
          className="w-full"
          value={forcedHighlight}
          onValueChange={(next) => {
            if (forcedHighlight !== undefined) setForcedHighlight(next);
          }}
          onKeyDown={handleListKeyDown}
          filter={(itemValue, searchText, keywords) =>
            itemValue.toLowerCase() === searchText.trim().toLowerCase()
              ? CREATE_ITEM_SCORE
              : defaultFilter(
                  itemValue.split(ITEM_VALUE_SEPARATOR)[0] ?? itemValue,
                  searchText,
                  keywords,
                )
          }
        >
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9 text-xs"
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandHighlightMirror onHighlightChange={setCmdkHighlight} />
          <CommandList onPointerMove={markNavigated}>
            {/* The create item, when offered, is itself the empty state. */}
            {!canCreate && <CommandEmpty>{emptyMessage}</CommandEmpty>}
            <CommandGroup>
              {allOptionLabel && (
                <CommandItem
                  value={allOptionLabel}
                  onSelect={() => {
                    onValueChange(allOptionValue);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      value === allOptionValue ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {allOptionLabel}
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  // Same-named options need a distinct cmdk value, or the
                  // list treats them as one item; the key keeps it unique
                  // without changing what the search matches on.
                  value={itemValue(opt)}
                  keywords={opt.keywords}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                  className="text-xs"
                  title={opt.title ?? opt.label}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex min-w-0 items-center gap-1.5">
                    {opt.leading ?? opt.avatar}
                    <span className="truncate">{opt.label}</span>
                    {opt.description && (
                      <span className="shrink-0 truncate text-muted-foreground">
                        {opt.description}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  // Matching the search text keeps this item visible however
                  // cmdk filters the list.
                  value={trimmedSearch}
                  onSelect={() => {
                    onCreateOption?.(trimmedSearch);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="text-xs"
                >
                  <Plus className="mr-2 h-3.5 w-3.5" />
                  <span className="truncate">
                    {createOptionLabel?.(trimmedSearch) ??
                      `Add "${trimmedSearch}"`}
                  </span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
